import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditTrackingService from '../../services/EditTrackingService';
import {formatUserName} from '../../utils/formatUtils';

export default function EditingLeaderboard({ folderPath, videoId, filterMode }) {
    const [leaderboard, setLeaderboard] = useState([]);
    const [detailedStats, setDetailedStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState({});
    const [orderBy, setOrderBy] = useState('totalTime');
    const [order, setOrder] = useState('asc');
    const [expandedUser, setExpandedUser] = useState(null);

    useEffect(() => {
        console.log('EditingLeaderboard: Folder path changed to:', folderPath);
        fetchLeaderboard();
    }, [folderPath, videoId, filterMode]);

    const fetchLeaderboard = async () => {
        setLoading(true);
        try {
            console.log('Fetching leaderboard with folder:', folderPath);
            // Pass filter parameters to the service
            let data;
            if (videoId) {
                // If video ID is provided, get stats specific to that video
                data = await EditTrackingService.getVideoContributors(videoId);
            } else {
                data = await EditTrackingService.getGlobalLeaderboard('time', folderPath);
            }
            console.log('Received leaderboard data:', data);
            setLeaderboard(data);
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDetails = async (userId, userLevel) => {
        const userKey = `${userId}-${userLevel}`;
        setDetailsLoading(prev => ({ ...prev, [userKey]: true }));
        
        try {
            // Fetch detailed stats for this user including video and language breakdown
            let detailedData;
            if (videoId) {
                // If viewing a specific video, get language breakdown for this video
                detailedData = await EditTrackingService.getUserVideoLanguageStats(userId, videoId);
            } else {
                // Otherwise get video breakdown
                detailedData = await EditTrackingService.getUserDetailedStats(userId, folderPath);
            }
            
            setDetailedStats(prev => ({
                ...prev,
                [userKey]: detailedData
            }));
        } catch (error) {
            console.error(`Error fetching details for user ${userId}:`, error);
        } finally {
            setDetailsLoading(prev => ({ ...prev, [userKey]: false }));
        }
    };

    const handleAccordionChange = (userId, userLevel) => (event, isExpanded) => {
        const userKey = `${userId}-${userLevel}`;
        setExpandedUser(isExpanded ? userKey : null);
        
        if (isExpanded && !detailedStats[userKey]) {
            fetchUserDetails(userId, userLevel);
        }
    };

    const formatTime = (ms) => {
        if (!ms) return '00:00:00';
        return EditTrackingService.formatTime(ms);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedLeaderboard = React.useMemo(() => {
        const stabilizedThis = leaderboard.map((el, index) => [el, index]);
        
        // Remove efficiency calculation from sorting
        stabilizedThis.sort((a, b) => {
            const aValue = a[0][orderBy] !== undefined ? a[0][orderBy] : 0;
            const bValue = b[0][orderBy] !== undefined ? b[0][orderBy] : 0;
            
            // Special case for totalTime: lower is better
            if (orderBy === 'totalTime') {
                if (aValue < bValue) {
                    return order === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return order === 'asc' ? 1 : -1;
                }
                return a[1] - b[1];
            } 
            
            // Default sorting for other fields (higher is better)
            if (bValue < aValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (bValue > aValue) {
                return order === 'asc' ? 1 : -1;
            }
            return a[1] - b[1]; // Maintain original order if values are equal
        });
        
        return stabilizedThis.map((el) => el[0]);
    }, [leaderboard, order, orderBy]);

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                    
                    {folderPath && folderPath !== '/' && (
                        <Typography variant="subtitle2" color="text.secondary">
                            Filtered by folder: {folderPath}
                        </Typography>
                    )}
                </Typography>
            </Box>
            
            {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            {/* Table head remains mostly the same */}
                            <TableHead>
                                <TableRow>
                                    <TableCell width="60px">#</TableCell>
                                    <TableCell>Editor</TableCell>
                                    <TableCell>Level</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'totalTime'}
                                            direction={orderBy === 'totalTime' ? order : 'asc'}
                                            onClick={() => handleRequestSort('totalTime')}
                                        >
                                            Total Time
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'totalEdits'}
                                            direction={orderBy === 'totalEdits' ? order : 'asc'}
                                            onClick={() => handleRequestSort('totalEdits')}
                                        >
                                            Total Edits
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'videosCount'}
                                            direction={orderBy === 'videosCount' ? order : 'asc'}
                                            onClick={() => handleRequestSort('videosCount')}
                                        >
                                            Videos
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'languagesCount'}
                                            direction={orderBy === 'languagesCount' ? order : 'asc'}
                                            onClick={() => handleRequestSort('languagesCount')}
                                        >
                                            Languages
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'sessionsCount'}
                                            direction={orderBy === 'sessionsCount' ? order : 'asc'}
                                            onClick={() => handleRequestSort('sessionsCount')}
                                        >
                                            Sessions
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLeaderboard.map((editor, index) => {
                                    const userKey = `${editor.userId}-${editor.userLevel}`;
                                    return (
                                        <React.Fragment key={userKey}>
                                            <TableRow 
                                                hover
                                                onClick={() => {
                                                    if (expandedUser !== userKey) {
                                                        setExpandedUser(userKey);
                                                        if (!detailedStats[userKey]) {
                                                            fetchUserDetails(editor.userId, editor.userLevel);
                                                        }
                                                    } else {
                                                        setExpandedUser(null);
                                                    }
                                                }}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': { 
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {index + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{formatUserName(editor.email)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={editor.userLevel}
                                                        color={editor.userLevel === 'Level 1' ? 'primary' : 'secondary'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{formatTime(editor.totalTime)}</TableCell>
                                                <TableCell>...</TableCell>
                                                <TableCell>{editor.videosCount || 0}</TableCell>
                                                <TableCell>{editor.languagesCount || 0}</TableCell>
                                                <TableCell>{editor.sessionsCount}</TableCell>
                                            </TableRow>
                                            
                                            {/* Detailed stats row */}
                                            <TableRow>
                                                <TableCell colSpan={8} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Accordion 
                                                        expanded={expandedUser === userKey}
                                                        onChange={handleAccordionChange(editor.userId, editor.userLevel)}
                                                        sx={{ boxShadow: 'none', background: 'rgba(0,0,0,0.02)' }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            sx={{ display: 'none' }}
                                                        >
                                                            <Typography>Details</Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            {detailsLoading[userKey] ? (
                                                                <Box display="flex" justifyContent="center" p={2}>
                                                                    <CircularProgress size={24} />
                                                                </Box>
                                                            ) : detailedStats[userKey] ? (
                                                                <Box>
                                                                    {/* Video-specific language breakdown */}
                                                                    {videoId && detailedStats[userKey].languageBreakdown && (
                                                                        <Box>
                                                                            <Typography variant="subtitle2" gutterBottom>
                                                                                Language Breakdown
                                                                            </Typography>
                                                                            <List dense>
                                                                                {Object.entries(detailedStats[userKey].languageBreakdown).map(([lang, stats]) => (
                                                                                    <ListItem key={lang} divider>
                                                                                        <ListItemText
                                                                                            primary={lang}
                                                                                            secondary={`${formatTime(stats.time)} `}
                                                                                        />
                                                                                    </ListItem>
                                                                                ))}
                                                                            </List>
                                                                        </Box>
                                                                    )}
                                                                    
                                                                    {/* Global stats - show video breakdown */}
                                                                    {!videoId && detailedStats[userKey].videoBreakdown && (
                                                                        <Box>
                                                                            <Typography variant="subtitle2" gutterBottom>
                                                                                Top 5 Videos
                                                                            </Typography>
                                                                            <List dense>
                                                                                {Object.entries(detailedStats[userKey].videoBreakdown)
                                                                                    .sort((a, b) => b[1].time - a[1].time)
                                                                                    .slice(0, 5)
                                                                                    .map(([videoId, stats]) => (
                                                                                        <ListItem key={videoId} divider>
                                                                                            <ListItemText
                                                                                                primary={stats.videoName || videoId}
                                                                                                secondary={`${formatTime(stats.time)}  • ${stats.languages.length} languages`}
                                                                                            />
                                                                                        </ListItem>
                                                                                    ))
                                                                                }
                                                                            </List>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No detailed data available
                                                                </Typography>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                                
                                {leaderboard.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
}